import { useQuery } from '@tanstack/react-query'
import { queryKeys } from '@sportninja/common/config/queryKeys'
import { listTeamRosters } from '@sportninja/common/services/team/listTeamRosters'
import { resolveRosterForDivision } from '@sportninja/common/features/competition/operations/utils/resolveRosterForDivision'
import type { TeamRosterSummary } from '@sportninja/common/features/competition/operations/utils/resolveRosterForDivision'
import { OPERATIONS_STALE_TIME } from './useOperationsTree'

interface Options {
  teamId: string | null
  divisionScheduleId: string | null
  enabled: boolean
}

/**
 * Loads a team's rosters and picks the one belonging to the division that is
 * currently selected.
 *
 * This is the ONLY place the Operations tab fetches roster data, and it runs
 * only when a team is selected — selecting a division never triggers per-team
 * requests.
 */
export const useSelectedTeamRoster = ({
  teamId,
  divisionScheduleId,
  enabled,
}: Options) => {
  'use memo'

  const query = useQuery({
    queryKey: queryKeys.operations.teamRosters(teamId ?? ''),
    queryFn: () => listTeamRosters(teamId as string),
    enabled: enabled && Boolean(teamId),
    staleTime: OPERATIONS_STALE_TIME,
  })

  const rosters: TeamRosterSummary[] = query.data ?? []
  const roster = divisionScheduleId
    ? resolveRosterForDivision(rosters, divisionScheduleId)
    : null

  return {
    roster,
    isLoading: query.isLoading,
    isError: query.isError,
  }
}
